import { useCallback, useEffect, type RefObject } from "react";
import {
  defaultRangeExtractor,
  measureElement,
  observeElementRect,
  useVirtualizer,
  type Range,
} from "@tanstack/react-virtual";

import type { InboxItem } from "./registry";

/** space.xs. Same as the old Flex gap. */
const CARD_GAP_PX = 2;
const CARD_ESTIMATE_PX = 112;
// shortcut: 640px flyout default, Jest ResizeObserver is a no-op
const LIST_INITIAL_RECT = { width: 380, height: 640 };

const useInboxListVirtualizer = (
  items: InboxItem[],
  selectedIndex: number,
  parentRef: RefObject<HTMLDivElement | null>
) => {
  const rangeExtractor = useCallback(
    (range: Range) => {
      const indexes = defaultRangeExtractor(range);
      if (selectedIndex >= 0 && !indexes.includes(selectedIndex)) {
        return [...indexes, selectedIndex].sort((a, b) => a - b);
      }
      return indexes;
    },
    [selectedIndex]
  );

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => CARD_ESTIMATE_PX,
    overscan: 8,
    gap: CARD_GAP_PX,
    getItemKey: (index) => items[index]?.key ?? index,
    initialRect: LIST_INITIAL_RECT,
    rangeExtractor,
    observeElementRect: (instance, cb) =>
      observeElementRect(instance, (rect) => {
        cb(
          rect.height < 1
            ? {
                width: Math.max(rect.width, LIST_INITIAL_RECT.width),
                height: LIST_INITIAL_RECT.height,
              }
            : rect
        );
      }),
    measureElement: (element, entry, instance) => {
      const size = measureElement(element, entry, instance);
      return size < 1 ? CARD_ESTIMATE_PX : size;
    },
  });

  useEffect(() => {
    if (selectedIndex < 0) {
      return;
    }
    virtualizer.scrollToIndex(selectedIndex, { align: "auto" });
  }, [selectedIndex, virtualizer]);

  return virtualizer;
};

export default useInboxListVirtualizer;
